<script setup lang="ts">
// ================================
// IMPORTS
// ================================
// Vue
import { computed, onBeforeMount, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"

// Store
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useAuthStore } from "@core/stores/auth.store"
import { useConfigStore } from "@core/stores/config.store"
import { storeToRefs } from "pinia"

// Types
import type { Source } from "@types"

// Composables
import { useRedirect } from "@core/composables"

// Constantes
import { METER_TYPES_LIST, WEATHER_STATION_TYPES_LIST } from "@core/constants"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Objets connectés",
            add_sensor: "Ajouter un capteur",
            return_to_list: "Retour à la liste des capteurs",
            empty_message:
                "Vous n'avez pas de capteurs associés à ce bâtiment.",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
// Aucune interface nécessaire, page autonome

// ================================
// PROPS & EMITS
// ================================
// Aucune props ni emits

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const { redirectWrapper } = useRedirect()
const buildingStore = useBuildingStore()
const authStore = useAuthStore()
const configStore = useConfigStore()

const {
    building,
    getSources,
    isFetchingBuilding,
    isFetchingBuildingSources,
    getBuildingIsViewOnly,
} = storeToRefs(buildingStore)

// ================================
// ÉTAT
// ================================
/**
 * Indique si on est en mode ajout de capteur
 */
const isAddingItem = ref<boolean>(false)

// ================================
// DONNÉES CALCULÉES
// ================================
/**
 * Identifiant du bâtiment depuis les paramètres de route
 */
const getBuildingId = computed<string | undefined>(() => {
    return route.params?._id_building as string | undefined
})

/**
 * Liste des objets connectés (capteurs IoT uniquement), filtrée et triée.
 * Exclut les compteurs, les sources SNC et les stations météo (page dédiée).
 */
const getConnectedObjects = computed<
    (Source & { disableModelImage?: boolean })[]
>(() => {
    if (!building.value) return []

    const sources = getSources.value || []

    const filtered = sources.filter((source) => {
        const type = source.type ?? source.idtype ?? ""
        const typeStr = typeof type === "string" ? type : ""

        if (METER_TYPES_LIST.includes(typeStr)) return false

        if (typeStr.includes("snc")) return false

        if (WEATHER_STATION_TYPES_LIST.includes(typeStr)) return false

        return true
    })

    return filtered
})

/**
 * Indique si l'utilisateur peut ajouter un capteur
 */
const getCanAddSensor = computed<boolean>(() => {
    return (
        authStore.isClientHomeys &&
        authStore.hasOfferEnergyManager &&
        authStore.isEmployeWriteOrHigher &&
        !getBuildingIsViewOnly.value
    )
})

/**
 * Indique si l'utilisateur a les droits pour éditer les sources.
 * C'est le cas si l'utilisateur est client Homeys, a l'offre Energy Manager, est employé en écriture ou supérieur et a le droit "owns" sur le bâtiment.
 */
const hasRightToEdit = computed<boolean>(() => {
    if (!building.value) return false
    if (getBuildingIsViewOnly.value) return false

    return (
        !!authStore.isClientHomeys &&
        !!authStore.hasOfferEnergyManager &&
        !!authStore.isEmployeWriteOrHigher &&
        Array.isArray(building.value.rights) &&
        building.value.rights.includes("owns")
    )
})

/**
 * Indique si on est en train de charger les données
 */
const getIsFetching = computed<boolean>(() => {
    return isFetchingBuilding.value || isFetchingBuildingSources.value
})

// ================================
// LOGIQUE MÉTIER
// ================================
/**
 * Gère le clic sur le bouton "Ajouter un capteur"
 * Active le mode ajout et nettoie la query
 */
function onClickAddSource(): void {
    isAddingItem.value = true
    router.push({
        path: `/building/${getBuildingId.value}/objet-connecte`,
    })
}

/**
 * Gère le retour à la liste
 * Désactive le mode ajout de capteur
 */
function onReturnToList(): void {
    isAddingItem.value = false
    router.push({
        path: `/building/${getBuildingId.value}/objet-connecte`,
    })
}

/**
 * Gère le clic sur le bouton "Données" d'une source
 * Navigue vers la page de données de la source
 */
function onClickData(payload: { id: string }): void {
    redirectWrapper("/source/:_id_source/data", { _id_source: payload.id })
}

/**
 * Gère le clic sur le bouton "Paramètres" d'une source (informations générales)
 */
function onClickSettings(payload: { id: string }): void {
    isAddingItem.value = false
    redirectWrapper("/source/:_id_source/info-general", {
        _id_source: payload.id,
    })
}

/**
 * Gère le changement de statut isUsed d'une source
 * Appelle updateBuildingSourceUsed pour mettre à jour le bâtiment
 */
async function onUpdateIsUsed(payload: {
    value: boolean
    sourceId: string
}): Promise<void> {
    if (!getBuildingId.value) return

    try {
        await buildingStore.updateBuildingSourceUsed({
            _id_building: getBuildingId.value,
            sourceId: payload.sourceId,
            isUsed: payload.value,
        })
    } catch (error) {
        console.error("Erreur lors de la mise à jour du statut isUsed:", error)
    }
}

/**
 * Gère la fin du formulaire capteur.
 * Rafraîchit le bâtiment et retourne à la liste.
 */
async function onDoneSensorForm(): Promise<void> {
    try {
        // Rafraîchit le bâtiment pour avoir les nouvelles sources
        if (getBuildingId.value) {
            buildingStore
                .fetchBuilding(getBuildingId.value, {
                    silent: true,
                })
                .then(() => {
                    buildingStore.fetchBuildingSources({
                        silent: true,
                        force: true,
                    })
                })
        }
    } catch (error) {
        console.error("Erreur lors du rafraîchissement du bâtiment:", error)
    } finally {
        // Retourne à la liste
        isAddingItem.value = false
    }
}

// ================================
// LIFECYCLE
// ================================
/**
 * Initialise les données au montage
 */
onBeforeMount(async () => {
    // Charge les opérateurs et les modèles de sources pour l'affichage dans ItemSource
    await Promise.all([
        configStore.fetchOperators(),
        configStore.fetchSourceModels(),
    ])
})
</script>

<template>
    <div class="page page-building-sensor-list">
        <!-- ================================ -->
        <!-- HEADER -->
        <!-- ================================ -->
        <HeaderPageBuilding
            :title="t('title')"
            class-header="flex justify-between items-center flex-wrap gap-4"
        >
            <div class="flex items-center gap-4">
                <BaseButton
                    v-if="isAddingItem"
                    icon="pi pi-arrow-left"
                    @click="onReturnToList"
                    text
                    severity="secondary"
                    :label="t('return_to_list')"
                >
                </BaseButton>
                <BaseButton
                    v-if="getCanAddSensor && !isAddingItem"
                    :label="t('add_sensor')"
                    severity="secondary"
                    icon="pi pi-plus"
                    @click="onClickAddSource"
                />
            </div>
        </HeaderPageBuilding>

        <!-- ================================ -->
        <!-- FORMULAIRE AJOUT -->
        <!-- ================================ -->

        <SensorForm
            v-if="
                isAddingItem &&
                getCanAddSensor &&
                getBuildingId &&
                building?.address
            "
            class="mt-8 mx-auto"
            :building-id="getBuildingId"
            :address="building.address"
            @done="onDoneSensorForm"
            @cancel="onReturnToList"
        />

        <!-- ================================ -->
        <!-- LISTE DES OBJETS CONNECTÉS -->
        <!-- ================================ -->
        <div v-else-if="!isAddingItem" class="mt-8">
            <!-- État de chargement -->
            <div v-if="getIsFetching" class="flex flex-col gap-4">
                <BaseSkeleton
                    v-for="index in 3"
                    :key="index"
                    width="100%"
                    height="6rem"
                />
            </div>

            <!-- Liste des sources -->
            <div
                v-else-if="getConnectedObjects.length > 0"
                class="flex flex-col gap-4"
            >
                <div
                    v-for="source in getConnectedObjects"
                    :key="source._id_source"
                    class="cursor-pointer"
                    @click="onClickData({ id: source._id_source })"
                >
                    <ItemSource
                        :source="source"
                        :building-id="getBuildingId"
                        :show-toggle-used="hasRightToEdit"
                        @click:data="onClickData"
                        @click:settings="onClickSettings"
                        @update:activate="onUpdateIsUsed"
                        :disable-model-image="source.disableModelImage"
                    >
                        <template #info="{ type, model, brand }">
                            <div
                                v-if="type === 'iot'"
                                class="flex gap-2 text-sm text-gray-500"
                            >
                                <p>|</p>
                                <p>{{ brand }} - {{ model }}</p>
                            </div>
                        </template>
                    </ItemSource>
                </div>
            </div>

            <!-- État vide -->
            <div
                v-else
                class="min-h-80 flex flex-col items-center justify-center gap-4 py-12"
            >
                <p class="text-gray-600 text-center">
                    {{ t("empty_message") }}
                </p>
            </div>
        </div>
    </div>
</template>
